import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { errorMessage, guestLogin } from "../api/client";
import { bootError } from "../state/boot";
import { AccountForm } from "../ui/components/AccountForm";
import { Button } from "../ui/components/Button";
import { DisplayPlate } from "../ui/components/DisplayPlate";
import { Field, TextInput } from "../ui/components/Field";
import { Spinner } from "../ui/components/Spinner";
import { completeSignIn, runBoot } from "./boot";
import { validateDisplayName } from "./validation";

function BootCard({ kicker, title, children }: { kicker: string; title: string; children: ComponentChildren }) {
  return (
    <section class="boot" aria-labelledby="boot-title">
      <DisplayPlate kicker={kicker} titleId="boot-title">
        {title}
      </DisplayPlate>
      <div class="boot__body">{children}</div>
    </section>
  );
}

/** Shown while the session and catalog load. Fixed size so the card never jumps. */
export function BootLoading() {
  return (
    <BootCard kicker="GUNRUN" title="Loading">
      <div class="boot__status" role="status">
        <Spinner size={22} />
        <span>Connecting to the server...</span>
      </div>
    </BootCard>
  );
}

/** Server unreachable or the catalog failed to load. */
export function BootError() {
  const [busy, setBusy] = useState(false);
  const retryButton = useRef<HTMLButtonElement>(null);
  useEffect(() => retryButton.current?.focus(), []);
  const retry = async () => {
    setBusy(true);
    try {
      await runBoot();
    } finally {
      setBusy(false);
    }
  };
  return (
    <BootCard kicker="Offline" title="Can't reach the server">
      <p class="boot__text">Check your connection and try again. Your progress is stored on the server and is not lost.</p>
      <div class="boot__status" role="alert">
        <div class="notice notice--error">{bootError.value || "Unknown error"}</div>
      </div>
      <Button variant="primary" size="lg" busy={busy} onClick={() => void retry()} ref={retryButton} full>
        Retry
      </Button>
    </BootCard>
  );
}

/** First run (or expired session): pick a name for a guest account, or log in. */
export function NamePrompt() {
  const [mode, setMode] = useState<"guest" | "login">("guest");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const nameError = validateDisplayName(name);

  const start = async () => {
    setSubmitted(true);
    setError("");
    if (nameError || busy) return;
    setBusy(true);
    try {
      await completeSignIn(await guestLogin(name.trim()));
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  if (mode === "login") {
    return (
      <BootCard kicker="Welcome back" title="Log in">
        <AccountForm mode="login" onDone={(res) => void completeSignIn(res)} />
        <Button variant="ghost" onClick={() => setMode("guest")} full>
          Play as a guest instead
        </Button>
      </BootCard>
    );
  }

  return (
    <BootCard kicker="First run" title="Pick your name">
      <p class="boot__text">
        Jump straight in as a guest. You can register later and keep every level, unlock and rating.
      </p>
      <Field label="Display name" hint="2-20 characters" error={submitted ? nameError : null}>
        {(id) => (
          <TextInput
            id={id}
            value={name}
            onValue={setName}
            maxLength={20}
            placeholder="Runner42"
            autoComplete="nickname"
            autoFocus
            invalid={submitted && nameError !== null}
            disabled={busy}
            onEnter={() => void start()}
          />
        )}
      </Field>
      <div class="boot__status" role="alert">
        {error && <div class="notice notice--error">{error}</div>}
      </div>
      <Button variant="primary" size="lg" busy={busy} onClick={() => void start()} full>
        Continue
      </Button>
      <Button variant="ghost" onClick={() => setMode("login")} full>
        I already have an account
      </Button>
    </BootCard>
  );
}
